import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { OperatorProfile, OperatorStatus } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupUsersProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'operator', label: 'Operator' },
];

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'available', label: 'Available' },
  { value: 'busy', label: 'Busy' },
  { value: 'away', label: 'Away' },
  { value: 'offline', label: 'Offline' },
];

const operatorStatusOptions = [
  { value: 'available', label: 'Available' },
  { value: 'busy', label: 'Busy' },
  { value: 'away', label: 'Away' },
  { value: 'offline', label: 'Offline' },
];

const SetupUsers: React.FC<SetupUsersProps> = ({ brand, Icons }) => {
  const { get, patch } = useApi();
  const [operators, setOperators] = useState<OperatorProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedOperator, setSelectedOperator] = useState<OperatorProfile | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<OperatorProfile>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchOperators = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (roleFilter !== 'all') params.set('role', roleFilter);

      const data = await get<{ operators: OperatorProfile[] }>(`/api/admin/operators?${params}`);
      // Filter by search client-side
      let fetchedOperators = data.operators || [];
      if (search) {
        const searchLower = search.toLowerCase();
        fetchedOperators = fetchedOperators.filter((op: OperatorProfile) =>
          op.displayName?.toLowerCase().includes(searchLower) ||
          op.email?.toLowerCase().includes(searchLower)
        );
      }
      setOperators(fetchedOperators);
    } catch (error) {
      console.error('Failed to fetch operators:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter, roleFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchOperators();
  }, [fetchOperators]);

  const handleRowClick = (operator: OperatorProfile) => {
    setSelectedOperator(operator);
    setFormData(operator);
    setActionResult(null);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxWorkload' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleStatusChange = async (status: OperatorStatus) => {
    if (!selectedOperator) return;

    setIsSaving(true);
    setActionResult(null);
    try {
      const data = await patch<{ operator: OperatorProfile }>(
        `/api/admin/operators/${selectedOperator.id}/status`,
        { status }
      );
      setActionResult({ type: 'success', message: 'Status updated successfully' });
      setFormData(data.operator);
      setSelectedOperator(data.operator);
      fetchOperators();
    } catch (error) {
      setActionResult({ type: 'error', message: error instanceof Error ? error.message : 'Failed to update status' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedOperator) return;

    setIsSaving(true);
    setActionResult(null);
    try {
      const data = await patch<{ operator: OperatorProfile }>(
        `/api/admin/operators/${selectedOperator.id}`,
        {
          displayName: formData.displayName,
          maxWorkload: formData.maxWorkload,
          platformRole: formData.platformRole,
        }
      );
      setActionResult({ type: 'success', message: 'Operator updated successfully' });
      setSelectedOperator(data.operator);
      fetchOperators();
    } catch (error) {
      setActionResult({ type: 'error', message: error instanceof Error ? error.message : 'Failed to update operator' });
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (operator: OperatorProfile) => (
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            {operator.displayName?.charAt(0) || '?'}
          </div>
          <div>
            <div className="font-medium" style={{ color: brand.colors.text }}>
              {operator.displayName}
            </div>
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {operator.email}
            </div>
          </div>
          <SetupItemPreviewPopover
            brand={brand}
            title={operator.displayName}
            fields={[
              { label: 'Email', value: operator.email },
              { label: 'Name', value: operator.displayName },
              { label: 'Role', value: operator.platformRole === 'admin' ? 'Admin' : 'Operator' },
              { label: 'Workload', value: `${operator.currentWorkload}/${operator.maxWorkload}` },
              { label: 'Status', value: operator.status },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (operator: OperatorProfile) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            operator.platformRole === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
          }`}
        >
          {operator.platformRole === 'admin' ? 'Admin' : 'Operator'}
        </span>
      ),
    },
    {
      key: 'workload',
      header: 'Workload',
      render: (operator: OperatorProfile) => (
        <div className="flex items-center gap-2">
          <div
            className="w-16 h-2 rounded-full bg-gray-200 overflow-hidden"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min((operator.currentWorkload / operator.maxWorkload) * 100, 100)}%`,
                backgroundColor: operator.currentWorkload >= operator.maxWorkload
                  ? '#EF4444'
                  : operator.currentWorkload >= operator.maxWorkload * 0.8
                    ? '#F59E0B'
                    : brand.colors.primary,
              }}
            />
          </div>
          <span className="text-xs" style={{ color: brand.colors.textLight }}>
            {operator.currentWorkload}/{operator.maxWorkload}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (operator: OperatorProfile) => <StatusBadge status={operator.status} />,
    },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search operators..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        categoryFilter={roleFilter}
        categoryOptions={[{ value: 'all', label: 'All Roles' }, ...roleOptions]}
        onCategoryChange={setRoleFilter}
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={operators}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No operators found"
        getRowId={(operator) => operator.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Edit Operator"
        subtitle={selectedOperator?.displayName}
        brand={brand}
        Icons={Icons}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        {/* Action Result Alert */}
        {actionResult && (
          <div
            className={`p-4 rounded-lg mb-6 ${
              actionResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {actionResult.message}
          </div>
        )}

        <div className="space-y-6">
          {/* Status Quick Actions */}
          <div>
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Quick Status
            </h3>
            <div className="flex gap-2">
              {operatorStatusOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusChange(opt.value as OperatorStatus)}
                  disabled={isSaving || formData.status === opt.value}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    formData.status === opt.value
                      ? ''
                      : 'border hover:bg-gray-50'
                  }`}
                  style={{
                    backgroundColor: formData.status === opt.value ? brand.colors.primary : undefined,
                    borderColor: formData.status === opt.value ? undefined : brand.colors.border,
                    color: formData.status === opt.value ? brand.colors.onPrimary : brand.colors.text,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Profile
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Display Name"
                name="displayName"
                value={formData.displayName || ''}
                onChange={handleInputChange}
                required
              />
              <FormSelect
                brand={brand}
                label="Role"
                name="platformRole"
                value={formData.platformRole || 'operator'}
                onChange={handleInputChange}
                options={roleOptions}
              />
            </div>

            <FormInput
              brand={brand}
              label="Email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleInputChange}
              disabled
              helpText="Email cannot be changed"
            />
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Workload Settings
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Max Workload"
                name="maxWorkload"
                type="number"
                value={formData.maxWorkload || 20}
                onChange={handleInputChange}
                helpText="Maximum concurrent work items"
              />
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                  Current Workload
                </div>
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: brand.colors.bgLight }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div
                        className="w-full h-3 rounded-full bg-gray-200 overflow-hidden"
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(((formData.currentWorkload || 0) / (formData.maxWorkload || 20)) * 100, 100)}%`,
                            backgroundColor: (formData.currentWorkload || 0) >= (formData.maxWorkload || 20)
                              ? '#EF4444'
                              : (formData.currentWorkload || 0) >= (formData.maxWorkload || 20) * 0.8
                                ? '#F59E0B'
                                : brand.colors.primary,
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
                      {formData.currentWorkload || 0}/{formData.maxWorkload || 20}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Additional Info
            </h3>
            <div>
              <div className="text-xs" style={{ color: brand.colors.textLight }}>Last Activity</div>
              <div className="text-sm" style={{ color: brand.colors.text }}>
                {formData.lastActivityAt
                  ? new Date(formData.lastActivityAt).toLocaleString()
                  : 'Never'
                }
              </div>
            </div>
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupUsers;
